/**
 * Nyx, the sales cat (Settings → Styling → Show Nyx). A small cat over every
 * console page: drag it anywhere, scroll over it to resize. When a tip or a
 * PPV purchase arrives on an Account the person watches (the Workspace
 * socket), it meows and shows "Anna tipped $20" beside it.
 *
 * This replaces the CRM's own Nyx (crm-settings.js), which only showed on the
 * old CRM pages; inside the console that one stays off. Position and size use
 * the CRM's keys, so a cat placed before keeps its place.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useStyling } from '../theme/userStyling';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import type { WorkspaceEvent } from '../workspace/activity';
import meowUrl from './assets/meow.ogg';
import './nyx.css';

const POSITION_KEY = 'crm_pet_nyx_position';
const SIZE_KEY = 'crm_pet_nyx_size';
export const NYX_SIZE = { min: 60, max: 180, step: 10, fallback: 100 } as const;
const PAD = 16;
const NOTICE_MS = 4200;
const MEOW_MS = 680;
const QUEUE_MAX = 8;

export interface PetPosition {
  /** 0..1 across the free width and height, so the cat keeps its place when the window resizes. */
  x: number;
  y: number;
}

export function normalizeNyxSize(value: unknown): number {
  const n = Number(value);
  const safe = Number.isFinite(n) && value !== null && value !== '' ? n : NYX_SIZE.fallback;
  const stepped = Math.round(safe / NYX_SIZE.step) * NYX_SIZE.step;
  return Math.max(NYX_SIZE.min, Math.min(NYX_SIZE.max, stepped));
}

function readPosition(): PetPosition {
  try {
    const saved = JSON.parse(localStorage.getItem(POSITION_KEY) || 'null');
    if (Number.isFinite(saved?.x) && Number.isFinite(saved?.y)) {
      return { x: Math.max(0, Math.min(1, saved.x)), y: Math.max(0, Math.min(1, saved.y)) };
    }
  } catch {
    /* ignore */
  }
  return { x: 0.66, y: 0.48 };
}

function readSize(): number {
  try {
    return normalizeNyxSize(localStorage.getItem(SIZE_KEY));
  } catch {
    return NYX_SIZE.fallback;
  }
}

function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

/** The cat's width in pixels for a size (percent) and window width. */
export function nyxWidth(size: number, viewportWidth: number): number {
  const base = Math.max(92, Math.min(136, viewportWidth * 0.11));
  return Math.round((base * size) / 100);
}

/** What the notice says for a sale event, or null for anything else. */
export function saleNotice(ev: WorkspaceEvent, t: (key: string, o?: Record<string, unknown>) => string): string | null {
  if (ev.kind !== 'tip' && ev.kind !== 'ppv_bought') return null;
  const name = String(ev.payload.fan_name ?? '').trim() || t('pet.fan');
  const label = String(ev.payload.amount_label ?? '').trim();
  const n = Number(ev.payload.amount);
  const amount = label || (Number.isFinite(n) && n > 0 ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '');
  const kind = ev.kind === 'tip' ? 'tip' : 'ppv';
  return amount ? t(`pet.${kind}`, { name, amount }) : t(`pet.${kind}NoAmount`, { name });
}

let meowAudio: HTMLAudioElement | null = null;
function meow() {
  try {
    if (!meowAudio) {
      meowAudio = new Audio(meowUrl);
      meowAudio.preload = 'auto';
      meowAudio.volume = 0.64;
      meowAudio.preservesPitch = false;
      meowAudio.defaultPlaybackRate = 1.28;
    }
    meowAudio.pause();
    meowAudio.currentTime = 0;
    meowAudio.playbackRate = 1.28;
    void meowAudio.play().catch(() => undefined);
  } catch {
    /* no audio */
  }
}

/** Shown only while signed in with Show Nyx on (the sprite sheets load only then). */
export function NyxHost() {
  const { flags } = useStyling();
  const { isAuthenticated } = useAuth();
  if (!flags.nyx || !isAuthenticated) return null;
  return createPortal(<Nyx />, document.body);
}

function Nyx() {
  const { t } = useTranslation('common');
  const ws = useWorkspace();
  const pet = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(readPosition);
  const [size, setSize] = useState(readSize);
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  const [drag, setDrag] = useState<{ id: number; dx: number; dy: number; lastX: number; left: boolean } | null>(null);
  const [dragAt, setDragAt] = useState<{ left: number; top: number } | null>(null);
  const [meowing, setMeowing] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [noticeAt, setNoticeAt] = useState<{ left: number; top: number } | null>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  const queue = useRef<string[]>([]);
  const showing = useRef(false);

  const width = nyxWidth(size, viewport.w);
  const height = Math.round((width * 208) / 192);
  const maxLeft = Math.max(PAD, viewport.w - width - PAD);
  const maxTop = Math.max(PAD, viewport.h - height - PAD);
  const placed = dragAt ?? { left: PAD + (maxLeft - PAD) * position.x, top: PAD + (maxTop - PAD) * position.y };

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll over the cat to resize it (a non-passive listener, so the page does not scroll too).
  useEffect(() => {
    const el = pet.current;
    if (!el) return;
    let delta = 0;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (delta && Math.sign(delta) !== Math.sign(e.deltaY)) delta = 0;
      delta += e.deltaY;
      if (Math.abs(delta) < 40) return;
      const step = delta < 0 ? NYX_SIZE.step : -NYX_SIZE.step;
      delta = 0;
      setSize((s) => {
        const next = normalizeNyxSize(s + step);
        store(SIZE_KEY, String(next));
        return next;
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const showNext = useCallback(() => {
    if (showing.current) return;
    const next = queue.current.shift();
    if (!next) return;
    showing.current = true;
    setNotice(next);
    setMeowing((n) => n + 1);
    meow();
    window.setTimeout(() => {
      setNotice(null);
      showing.current = false;
      if (queue.current.length) window.setTimeout(showNext, 180);
    }, NOTICE_MS);
  }, []);

  // Sales from the Workspace socket. Nothing queues while the tab is hidden.
  useEffect(
    () =>
      ws.onEvent((ev) => {
        const text = saleNotice(ev, t);
        if (!text || document.hidden) return;
        queue.current.push(text);
        if (queue.current.length > QUEUE_MAX) queue.current.shift();
        showNext();
      }),
    [ws, t, showNext],
  );

  useEffect(() => {
    if (!meowing) return;
    const timer = window.setTimeout(() => setMeowing(0), MEOW_MS);
    return () => window.clearTimeout(timer);
  }, [meowing]);

  // The notice sits under the cat, or above it near the bottom edge, inside the window.
  useLayoutEffect(() => {
    const box = noticeRef.current;
    if (!notice || !box) return setNoticeAt(null);
    const half = box.offsetWidth / 2;
    const left = Math.max(PAD + half, Math.min(viewport.w - PAD - half, placed.left + width / 2));
    let top = placed.top + height + 10;
    if (top + box.offsetHeight > viewport.h - PAD) top = Math.max(PAD, placed.top - box.offsetHeight - 10);
    setNoticeAt({ left, top });
  }, [notice, placed.left, placed.top, width, height, viewport]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    setDrag({ id: e.pointerId, dx: e.clientX - placed.left, dy: e.clientY - placed.top, lastX: e.clientX, left: false });
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag || e.pointerId !== drag.id) return;
    const moved = e.clientX - drag.lastX;
    if (Math.abs(moved) > 1) setDrag({ ...drag, lastX: e.clientX, left: moved < 0 });
    setDragAt({
      left: Math.max(PAD, Math.min(maxLeft, e.clientX - drag.dx)),
      top: Math.max(PAD, Math.min(maxTop, e.clientY - drag.dy)),
    });
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag || e.pointerId !== drag.id) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (dragAt) {
      const next = {
        x: maxLeft === PAD ? 0 : (dragAt.left - PAD) / (maxLeft - PAD),
        y: maxTop === PAD ? 0 : (dragAt.top - PAD) / (maxTop - PAD),
      };
      setPosition(next);
      store(POSITION_KEY, JSON.stringify(next));
    }
    setDrag(null);
    setDragAt(null);
  };

  return (
    <>
      <div
        ref={pet}
        className={['om-pet', drag ? 'is-dragging' : '', drag?.left ? 'is-moving-left' : '', meowing ? 'is-meowing' : ''].join(' ')}
        style={{ left: Math.round(placed.left), top: Math.round(placed.top), width }}
        title={t('pet.title')}
        aria-hidden="true"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      {notice && (
        <div
          ref={noticeRef}
          className="om-pet-notice"
          role="status"
          aria-live="polite"
          style={noticeAt ? { left: Math.round(noticeAt.left), top: Math.round(noticeAt.top) } : { visibility: 'hidden', left: 0, top: 0 }}
        >
          {notice}
        </div>
      )}
    </>
  );
}
